import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ApiService } from "@app/api/api.service";
import {
  ApiId,
  TOOLBOX_TALK_MODEL,
  ToolboxTalk,
  ToolboxTalkApiValue,
  ToolboxTalkAttendee,
  ToolboxTalkFormValue,
  ToolboxTalkItem,
  createItemsFromTemplate,
} from "@app/models";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Action, Store } from "@ngrx/store";
import { box, unbox } from "ngrx-forms";
import { NavController } from "@ionic/angular";
import { concat, Observable, of } from "rxjs";
import {
  catchError,
  exhaustMap,
  filter,
  map,
  switchMap,
  tap,
} from "rxjs/operators";
import { OfflineQueueApiActions } from "../actions";
import { getApiId, getRouteId } from "../model";
import {
  OperationApiActions,
  OperationViewPageActions,
} from "../operations/actions";
import {
  selectDefaultToolboxTalkTemplate,
  selectLoggedUser,
  selectOperation,
  selectOperationSyncOptions,
  selectToolboxTalk,
  selectToolboxTalkForm,
  selectToolboxTalkId,
  selectToolboxTalkTemplate,
} from "../selectors";
import {
  ToolboxTalkApiActions,
  ToolboxTalkListPageActions,
  ToolboxTalkPutPageActions,
  ToolboxTalkViewPageActions,
} from "./actions";

@Injectable()
export class ToolboxTalkEffects {
  private store = inject(Store);
  private router = inject(Router);
  private navController = inject(NavController);
  private apiService = inject(ApiService);
  private actions$ = inject(Actions);

  url = ["/", "toolbox-talks"];

  constructor() {}

  searchOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperationApiActions.searchOperationsSuccess),
      map(() => ToolboxTalkListPageActions.searchToolboxTalks())
    )
  );

  searchToolboxTalks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ToolboxTalkListPageActions.searchToolboxTalks),
      switchMap(() =>
        this.apiService.search(TOOLBOX_TALK_MODEL).pipe(
          map((apiData) =>
            ToolboxTalkApiActions.searchToolboxTalksSuccess({
              toolboxTalks: apiData.map((item) => new ToolboxTalk(item)),
            })
          ),
          catchError((error) =>
            of(ToolboxTalkApiActions.searchToolboxTalksFailure({ error }))
          )
        )
      )
    )
  );

  openOrCreateToolboxTalk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperationViewPageActions.openToolboxTalks),
      concatLatestFrom(() => [
        this.store.select(selectLoggedUser),
        this.store.select(selectOperation),
        this.store.select(selectDefaultToolboxTalkTemplate),
        this.store.select(selectOperationSyncOptions),
      ]),
      exhaustMap(([, user, operation, template, sync]) =>
        this.apiService
          .searchRelated(TOOLBOX_TALK_MODEL, "operation_id", operation)
          .pipe(
          map((apiData) => apiData.map((item) => new ToolboxTalk(item))),
          switchMap((toolboxTalks) => {
            const loaded = ToolboxTalkApiActions.searchRelatedToolboxTalksSuccess({
              toolboxTalks,
              operationIds: [operation._id, operation.id].filter(
                (id): id is ApiId => id !== null && id !== undefined
              ),
            });
            let decision$: Observable<Action>;
            if (toolboxTalks.length > 1) {
              decision$ = of(
                ToolboxTalkApiActions.listToolboxTalks({
                  operationId: getRouteId(operation),
                })
              );
            } else if (toolboxTalks.length === 1) {
              decision$ = of(
                ToolboxTalkApiActions.viewToolboxTalk({
                  toolboxTalk: toolboxTalks[0],
                })
              );
            } else if (!template) {
              decision$ = of(
                ToolboxTalkApiActions.createToolboxTalkFailure({
                  error:
                    "No default Toolbox Talk template is configured for this company.",
                })
              );
            } else {
              const values = new ToolboxTalkApiValue({
                operation_id: getApiId(operation),
                template_id: box(template),
                speaker_name: user.name,
                speaker_position: "Supervisor",
                item_ids: createItemsFromTemplate(
                  template.item_ids,
                  (item) => new ToolboxTalkItem(item)
                ),
                attendee_ids: operation.diver_ids.map(
                  (diver) =>
                    new ToolboxTalkAttendee({
                      name: diver.name,
                      position: "Diver",
                    })
                ),
              });
              decision$ = this.apiService
                .create(TOOLBOX_TALK_MODEL, values, sync)
                .pipe(
                  map((apiData) =>
                    ToolboxTalkApiActions.createToolboxTalkSuccess({
                      toolboxTalk: new ToolboxTalk(apiData),
                    })
                  ),
                  catchError((error) =>
                    of(
                      ToolboxTalkApiActions.createToolboxTalkFailure({ error })
                    )
                  )
                );
            }
            return concat(of(loaded), decision$);
          }),
          catchError((error) =>
            of(ToolboxTalkApiActions.searchToolboxTalksFailure({ error }))
          )
        )
      )
    )
  );

  openToolboxTalkListPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ToolboxTalkApiActions.listToolboxTalks),
        tap(({ operationId }) =>
          this.router.navigate([...this.url, "operation", operationId])
        )
      ),
    { dispatch: false }
  );

  openToolboxTalkViewPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ToolboxTalkApiActions.viewToolboxTalk,
          ToolboxTalkApiActions.createToolboxTalkSuccess,
          ToolboxTalkApiActions.updateToolboxTalkSuccess
        ),
        tap((action) => {
          const toolboxTalkId = getRouteId(action.toolboxTalk);
          const { type } = action;
          const viewUrl = [...this.url, "view", toolboxTalkId];
          if (type === ToolboxTalkApiActions.updateToolboxTalkSuccess.type) {
            void this.navController.navigateBack(viewUrl);
            return;
          }
          void this.router.navigate(viewUrl);
        })
      ),
    { dispatch: false }
  );

  openToolboxTalkEditPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ToolboxTalkViewPageActions.updateToolboxTalk),
        concatLatestFrom(() => this.store.select(selectToolboxTalkId)),
        tap(([, toolboxTalkId]) => {
          this.router.navigate([...this.url, "edit", toolboxTalkId]);
        })
      ),
    { dispatch: false }
  );

  setOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ToolboxTalkViewPageActions.setToolboxTalk,
        ToolboxTalkPutPageActions.setToolboxTalk
      ),
      concatLatestFrom(() => this.store.select(selectToolboxTalk)),
      map(([, { operation_id: operationId }]) =>
        ToolboxTalkApiActions.setOperation({ operationId })
      )
    )
  );

  setToolboxTalkTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ToolboxTalkPutPageActions.setTemplate),
      concatLatestFrom(() => [
        this.store.select(selectToolboxTalkForm),
        this.store.select(selectToolboxTalkTemplate),
      ]),
      map(([, form, template]) =>
        ToolboxTalkApiActions.setToolboxTalkTemplate({
          toolboxTalkFormValue: new ToolboxTalkFormValue({
            ...unbox(form.value),
            item_ids: createItemsFromTemplate(
              template.item_ids,
              (item) => new ToolboxTalkItem(item)
            ),
          }),
        })
      )
    )
  );

  setToolboxTalkForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ToolboxTalkPutPageActions.setToolboxTalk),
      concatLatestFrom(() => this.store.select(selectToolboxTalk)),
      map(([, toolboxTalk]) =>
        ToolboxTalkApiActions.setToolboxTalkForm({
          toolboxTalkFormValue: new ToolboxTalkFormValue(toolboxTalk),
        })
      )
    )
  );

  updateToolboxTalk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ToolboxTalkPutPageActions.updateToolboxTalk),
      concatLatestFrom(() => [
        this.store.select(selectToolboxTalk),
        this.store.select(selectToolboxTalkForm),
        this.store.select(selectOperationSyncOptions),
      ]),
      exhaustMap(([, toolboxTalk, form, sync]) => {
        if (!form.isValid) {
          return of(ToolboxTalkApiActions.toolboxTalkFormInvalid());
        }
        const values = new ToolboxTalkApiValue(form.value);
        return this.apiService
          .update(TOOLBOX_TALK_MODEL, toolboxTalk._id, values, sync)
          .pipe(
            map((apiData) =>
              ToolboxTalkApiActions.updateToolboxTalkSuccess({
                toolboxTalk: new ToolboxTalk(apiData),
              })
            ),
            catchError((error) =>
              of(ToolboxTalkApiActions.updateToolboxTalkFailure({ error }))
            )
          );
      })
    )
  );

  syncToolboxTalkSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        OfflineQueueApiActions.ApiCreateSuccess,
        OfflineQueueApiActions.ApiUpdateSuccess
      ),
      filter(({ model }) => model == TOOLBOX_TALK_MODEL),
      map(({ data }) =>
        ToolboxTalkApiActions.syncToolboxTalkSuccess({
          toolboxTalk: new ToolboxTalk(data),
        })
      )
    )
  );
}
